import { Link } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Trophy, Target, Flame, RotateCcw, CheckCircle2, XCircle, Eye } from 'lucide-react'
import { useGame } from '../context/GameContext'
import { categories } from '../data/categories'
import { vocabulary } from '../data/vocabulary'
import { backfillCategoryStats, loginPlayer, type PlayerData } from '../utils/api'

interface CategoryComputedStat {
  categoryId: string
  totalUniqueWords: number
  uniqueSeen: number
  uniqueCompleted: number
  attempts: number
  correct: number
  wrong: number
  accuracy: number
}

export default function Progress() {
  const { progress, resetProgress } = useGame()
  const [playerData, setPlayerData] = useState<PlayerData | null>(null)

  useEffect(() => {
    const pw = localStorage.getItem('hsk2-current-player')
    if (!pw) return
    loginPlayer(pw).then(async (data) => {
      setPlayerData(data)

      const hasRemoteCategoryStats = (data.categoryStats || []).length > 0
      if (hasRemoteCategoryStats) return

      // One-time backfill from local progress snapshot to D1 category stats
      const key = `hsk2-category-backfilled-${pw}`
      if (localStorage.getItem(key) === '1') return

      const computed = categories.map(cat => {
        const categoryWordIds = vocabulary.filter(v => v.category === cat.id).map(v => v.id)
        const attempts = categoryWordIds.reduce((sum, id) => sum + (progress.wordStats[id]?.correct || 0) + (progress.wordStats[id]?.wrong || 0), 0)
        const correct = categoryWordIds.reduce((sum, id) => sum + (progress.wordStats[id]?.correct || 0), 0)
        const wrong = categoryWordIds.reduce((sum, id) => sum + (progress.wordStats[id]?.wrong || 0), 0)
        const uniqueSeen = categoryWordIds.filter(id => (progress.wordStats[id]?.correct || 0) + (progress.wordStats[id]?.wrong || 0) > 0).length
        return { categoryId: cat.id, uniqueSeen, attempts, correct, wrong }
      }).filter(s => s.attempts > 0 || s.uniqueSeen > 0)

      if (computed.length === 0) {
        localStorage.setItem(key, '1')
        return
      }

      const result = await backfillCategoryStats(pw, computed)
      if (result?.success) {
        localStorage.setItem(key, '1')
        const refreshed = await loginPlayer(pw)
        setPlayerData(refreshed)
      }
    }).catch(() => setPlayerData(null))
  }, [progress.wordStats])

  const categoryStats = useMemo<CategoryComputedStat[]>(() => {
    const remoteStatsMap = new Map((playerData?.categoryStats || []).map(s => [s.categoryId, s]))

    return categories.map(cat => {
      const categoryWords = vocabulary.filter(w => w.category === cat.id)
      const totalUniqueWords = categoryWords.length
      const categoryWordIds = categoryWords.map(w => w.id)

      const localAttempts = categoryWordIds.reduce((sum, id) => {
        const stat = progress.wordStats[id]
        return sum + (stat?.correct || 0) + (stat?.wrong || 0)
      }, 0)

      const localCorrect = categoryWordIds.reduce((sum, id) => sum + (progress.wordStats[id]?.correct || 0), 0)
      const localWrong = categoryWordIds.reduce((sum, id) => sum + (progress.wordStats[id]?.wrong || 0), 0)
      const localUniqueSeen = categoryWordIds.filter(id => {
        const s = progress.wordStats[id]
        return (s?.correct || 0) + (s?.wrong || 0) > 0
      }).length

      const remote = remoteStatsMap.get(cat.id)
      const attempts = Math.max(localAttempts, remote?.attempts || 0)
      const correct = Math.max(localCorrect, remote?.correct || 0)
      const wrong = Math.max(localWrong, remote?.wrong || 0)
      const uniqueSeen = Math.max(localUniqueSeen, remote?.uniqueSeen || 0)
      const uniqueCompleted = uniqueSeen >= totalUniqueWords ? totalUniqueWords : uniqueSeen
      const accuracy = attempts > 0 ? Math.round((correct / attempts) * 100) : 0

      return {
        categoryId: cat.id,
        totalUniqueWords,
        uniqueSeen,
        uniqueCompleted,
        attempts,
        correct,
        wrong,
        accuracy,
      }
    })
  }, [playerData?.categoryStats, progress.wordStats])

  const totals = useMemo(() => {
    const totalUniqueWords = vocabulary.length
    const uniqueSeen = categoryStats.reduce((sum, s) => sum + s.uniqueSeen, 0)
    const attempts = categoryStats.reduce((sum, s) => sum + s.attempts, 0)
    const correct = categoryStats.reduce((sum, s) => sum + s.correct, 0)
    const wrong = categoryStats.reduce((sum, s) => sum + s.wrong, 0)
    const accuracy = attempts > 0 ? Math.round((correct / attempts) * 100) : 0

    return {
      totalUniqueWords,
      uniqueSeen,
      attempts,
      correct,
      wrong,
      accuracy,
      seenPercent: Math.round((uniqueSeen / totalUniqueWords) * 100),
    }
  }, [categoryStats])

  const handleReset = () => {
    if (confirm('Reset local progress on this device?')) {
      resetProgress()
    }
  }

  return (
    <div className="page-shell bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 p-3 md:p-4 flex flex-col">
      <div className="max-w-6xl mx-auto w-full flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <Link
            to="/"
            className="bg-white/20 rounded-2xl p-2.5 md:p-3 active:scale-95 transition-transform"
          >
            <ArrowLeft className="w-6 h-6 md:w-8 md:h-8 text-white" />
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Progress</h1>
          <button
            onClick={handleReset}
            className="bg-white/20 rounded-2xl p-2.5 md:p-3 active:scale-95 transition-transform"
          >
            <RotateCcw className="w-6 h-6 md:w-8 md:h-8 text-white" />
          </button>
        </div>

        <div className="flex-1 flex flex-col lg:flex-row gap-4 min-h-0">
          {/* Left: Overview */}
          <div className="w-full lg:w-1/3 flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-3 md:p-4 text-center">
                <Trophy className="w-6 h-6 md:w-8 md:h-8 text-yellow-300 mx-auto mb-1" />
                <div className="text-2xl md:text-3xl font-bold text-white break-words">{progress.totalScore}</div>
                <div className="text-white/80 text-xs md:text-sm">Score</div>
              </div>
              <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-3 md:p-4 text-center">
                <Flame className="w-6 h-6 md:w-8 md:h-8 text-orange-300 mx-auto mb-1" />
                <div className="text-2xl md:text-3xl font-bold text-white break-words">{progress.maxStreak}</div>
                <div className="text-white/80 text-xs md:text-sm">Best Streak</div>
              </div>
              <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-3 md:p-4 text-center">
                <Eye className="w-6 h-6 md:w-8 md:h-8 text-cyan-300 mx-auto mb-1" />
                <div className="text-2xl md:text-3xl font-bold text-white break-words">{totals.uniqueSeen}</div>
                <div className="text-white/80 text-xs md:text-sm">Unique Seen</div>
              </div>
              <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-3 md:p-4 text-center">
                <Target className="w-6 h-6 md:w-8 md:h-8 text-green-300 mx-auto mb-1" />
                <div className="text-2xl md:text-3xl font-bold text-white break-words">{totals.accuracy}%</div>
                <div className="text-white/80 text-xs md:text-sm">Accuracy</div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-xl">
              <div className="flex justify-between items-center mb-2 gap-2">
                <h2 className="text-base md:text-lg font-bold text-gray-800">Overall Coverage</h2>
                <span className="text-lg md:text-xl font-bold text-orange-500 whitespace-nowrap">
                  {totals.seenPercent}%
                </span>
              </div>
              <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-orange-500 to-red-500 h-full rounded-full transition-all duration-500"
                  style={{ width: `${(totals.uniqueSeen / totals.totalUniqueWords) * 100}%` }}
                />
              </div>
              <p className="text-gray-500 mt-1 text-sm">
                Unique seen: {totals.uniqueSeen} / {totals.totalUniqueWords}
              </p>
              <p className="text-gray-500 text-sm">Attempts: {totals.attempts} · ✅ {totals.correct} · ❌ {totals.wrong}</p>
            </div>
          </div>

          {/* Right: Category breakdown */}
          <div className="flex-1 bg-white rounded-2xl p-4 shadow-xl overflow-y-auto min-h-0">
            <h2 className="text-base md:text-lg font-bold text-gray-800 mb-3">By Category</h2>
            <div className="space-y-3">
              {categories.map((cat) => {
                const stat = categoryStats.find(s => s.categoryId === cat.id)!
                const coveragePercent = stat.totalUniqueWords > 0 ? Math.round((stat.uniqueSeen / stat.totalUniqueWords) * 100) : 0
                const completedAll = stat.uniqueSeen >= stat.totalUniqueWords && stat.totalUniqueWords > 0

                return (
                  <div key={cat.id} className="border border-gray-200 rounded-2xl p-3">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`${cat.color} rounded-xl p-2 text-lg`}>{cat.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="font-semibold text-sm md:text-base truncate">{cat.nameEn}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${completedAll ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {completedAll ? 'Complete' : `${coveragePercent}% seen`}
                          </span>
                        </div>
                        <div className="bg-gray-200 rounded-full h-2 overflow-hidden mt-1">
                          <div className={`${cat.color} h-full rounded-full transition-all duration-300`} style={{ width: `${coveragePercent}%` }} />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
                      <div className="bg-gray-50 rounded-lg px-2 py-1"><span className="text-gray-500">Unique total</span><div className="font-bold">{stat.totalUniqueWords}</div></div>
                      <div className="bg-gray-50 rounded-lg px-2 py-1"><span className="text-gray-500">Unique seen</span><div className="font-bold">{stat.uniqueSeen}</div></div>
                      <div className="bg-gray-50 rounded-lg px-2 py-1"><span className="text-gray-500">Attempts</span><div className="font-bold">{stat.attempts}</div></div>
                      <div className="bg-green-50 rounded-lg px-2 py-1"><span className="text-green-700">Correct</span><div className="font-bold text-green-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{stat.correct}</div></div>
                      <div className="bg-red-50 rounded-lg px-2 py-1"><span className="text-red-700">Wrong</span><div className="font-bold text-red-700 flex items-center gap-1"><XCircle className="w-3 h-3" />{stat.wrong}</div></div>
                      <div className="bg-blue-50 rounded-lg px-2 py-1"><span className="text-blue-700">Accuracy</span><div className="font-bold text-blue-700">{stat.accuracy}%</div></div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
